/**
 * Gallery.js — Premium Art Gallery Theme Interactions
 * ====================================================
 * Vanilla JavaScript for all animations and interactions.
 * Targets existing Shopify Debut theme markup.
 *
 * Features:
 * - Page load fade-in animation
 * - Scroll reveal via IntersectionObserver
 * - Parallax effect on hero images
 * - Sticky header with backdrop blur on scroll
 * - Cart icon pulse on add-to-cart
 *
 * All animations respect prefers-reduced-motion.
 */

(function() {
  'use strict';

  // ===== REDUCED MOTION CHECK =====
  // Respect the user's OS-level motion preference
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;


  // ===== DOM READY =====
  document.addEventListener('DOMContentLoaded', function() {

    // --- Page Load Animation ---
    // Use double-rAF to ensure the browser has painted the initial state
    // before triggering the transition to visible
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        document.body.classList.add('page-loaded');
      });
    });

    // --- Initialize motion-dependent features ---
    if (!prefersReducedMotion) {
      initScrollReveal();
      initParallax();
    }

    // --- Always-on features ---
    initStickyHeader();
    initCartPulse();
    initMediaCards();
    initGalleryFilter();
    initContactFix();
  });


  // ===================================================================
  // CONTACT PAGE FIX (Auto-adds phone number for WhatsApp/Line)
  // ===================================================================
  function initContactFix() {
    var icons = document.querySelectorAll('.rte img');
    for (var i = 0; i < icons.length; i++) {
      var img = icons[i];
      var src = img.src.toLowerCase();
      var link = img.closest ? img.closest('a') : img.parentNode;
      
      if (link && link.tagName === 'A') {
        var href = link.href.toLowerCase();
        
        if (src.indexOf('whatsapp') > -1 || src.indexOf('line') > -1 || href.indexOf('wa.me') > -1 || href.indexOf('line.me') > -1) {
          // Disable the broken link behavior
          link.addEventListener('click', function(e) {
            e.preventDefault();
          });
          link.style.cursor = 'default';
          
          // Append the phone number instruction right after the link
          if (!link.nextElementSibling || link.nextElementSibling.className !== 'contact-phone-text') {
            var text = document.createElement('div');
            text.className = 'contact-phone-text';
            text.innerText = 'Please add this phone number: +886 966109082';
            text.style.fontFamily = 'var(--font-body, sans-serif)';
            text.style.fontSize = '0.9rem';
            text.style.color = 'var(--color-accent, #042d0d)';
            text.style.fontWeight = 'bold';
            text.style.marginTop = '-0.5rem';
            text.style.marginBottom = '1.5rem';
            text.style.letterSpacing = '0.05em';
            
            link.parentNode.insertBefore(text, link.nextSibling);
          }
        } else if (src.indexOf('messenger') > -1 || src.indexOf('facebook') > -1 || href.indexOf('messenger.com') > -1 || href.indexOf('m.me') > -1) {
          // Fix the broken messenger link by redirecting to Facebook
          link.href = 'https://www.facebook.com/AnahataArtisan/';
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.cursor = 'pointer';
        }
      }
    }
  }


  // ===================================================================
  // SCROLL REVEAL (Intersection Observer)
  // ===================================================================
  // Adds the `.gallery-reveal` class to key sections and product cards,
  // then observes them. When an element enters the viewport it receives
  // `.gallery-reveal--visible` which triggers the CSS transition.
  // Each element is unobserved after reveal so the animation fires once.
  // ===================================================================

  function initScrollReveal() {
    // Selectors for elements that should animate on scroll
    var revealSelectors = [
      '.grid-product',
      '.index-section',
      '.site-footer__section',
      '.flex__item',
      '.feature-row',
      '.section-header',
      '.collection-hero',
      '.rte-setting'
    ];

    // Tag each matching element with the reveal class
    revealSelectors.forEach(function(selector) {
      var elements = document.querySelectorAll(selector);
      for (var i = 0; i < elements.length; i++) {
        elements[i].classList.add('gallery-reveal');
      }
    });

    // Bail if IntersectionObserver is not supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: make everything visible immediately
      var fallbackEls = document.querySelectorAll('.gallery-reveal');
      for (var f = 0; f < fallbackEls.length; f++) {
        fallbackEls[f].classList.add('gallery-reveal--visible');
      }
      return;
    }

    // Create the observer
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('gallery-reveal--visible');
          observer.unobserve(entry.target); // Only animate once
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    // Observe all tagged elements
    var revealEls = document.querySelectorAll('.gallery-reveal');
    for (var r = 0; r < revealEls.length; r++) {
      observer.observe(revealEls[r]);
    }
  }


  // ===================================================================
  // GALLERY FILTER TOGGLE (Client-side filtering for products)
  // ===================================================================
  function initGalleryFilter() {
    // Look for product cards on the page
    var productCards = document.querySelectorAll('.grid-product, .collection-grid-item, .card-wrapper, .grid-view-item');
    if (productCards.length === 0) return;

    // Find the main grid container to inject the toggle above it
    var firstProduct = productCards[0];
    var gridContainer = firstProduct.closest('.grid, .collection-grid, ul, .page-width');
    if (!gridContainer) gridContainer = firstProduct.parentNode;

    // Check if toggle already exists or if we're not on a collection page
    if (document.querySelector('.gallery-filter-toggle')) return;

    // Build the toggle HTML
    var toggle = document.createElement('div');
    toggle.className = 'gallery-filter-toggle gallery-reveal';
    toggle.innerHTML = 
      '<button class="gallery-filter-btn active" data-filter="all">All Creations</button>' +
      '<button class="gallery-filter-btn" data-filter="paintings">Paintings</button>' +
      '<button class="gallery-filter-btn" data-filter="jewelry">Jewelry</button>';

    // Insert it right above the grid
    gridContainer.parentNode.insertBefore(toggle, gridContainer);

    var buttons = toggle.querySelectorAll('.gallery-filter-btn');
    
    // Convert NodeList to Array for IE11 compatibility if needed, but forEach usually works in modern browsers
    for (var b = 0; b < buttons.length; b++) {
      buttons[b].addEventListener('click', function() {
        var clickedBtn = this;
        var filter = clickedBtn.getAttribute('data-filter');

        // Update button states
        for (var j = 0; j < buttons.length; j++) {
          buttons[j].classList.remove('active');
        }
        clickedBtn.classList.add('active');

        // Filter the products
        for (var p = 0; p < productCards.length; p++) {
          var card = productCards[p];
          // Determine the parent wrapper to hide/show (like the <li> or .grid__item)
          var wrapper = card.closest('.grid__item, li') || card;
          
          var titleEl = card.querySelector('.grid-product__title, .card__heading, .grid-view-item__title');
          var title = titleEl ? titleEl.innerText.toLowerCase() : '';
          
          // Basic keyword matching
          var isJewelry = title.indexOf('jewelry') > -1 || 
                          title.indexOf('necklace') > -1 || 
                          title.indexOf('pendant') > -1 || 
                          title.indexOf('earring') > -1 || 
                          title.indexOf('bracelet') > -1 || 
                          title.indexOf('ring') > -1;
                          
          var isPainting = !isJewelry; // Assume all other handmade creations are paintings/art

          // Apply visibility
          if (filter === 'all') {
            wrapper.style.display = '';
            // Reset opacity for animations
            wrapper.style.opacity = '1'; 
            wrapper.style.transform = 'none';
          } else if (filter === 'paintings' && isPainting) {
            wrapper.style.display = '';
            wrapper.style.opacity = '1';
            wrapper.style.transform = 'none';
          } else if (filter === 'jewelry' && isJewelry) {
            wrapper.style.display = '';
            wrapper.style.opacity = '1';
            wrapper.style.transform = 'none';
          } else {
            wrapper.style.display = 'none';
          }
        }
      });
    }
  }


  // ===================================================================
  // PARALLAX EFFECT
  // ===================================================================
  // Applies a subtle vertical parallax to hero / slideshow images.
  // Uses requestAnimationFrame for smooth 60fps performance.
  // Only runs when the hero section is in the viewport.
  // ===================================================================

  function initParallax() {
    var heroImages = document.querySelectorAll(
      '.hero__image, .hero .hero__image-wrapper img, .slideshow__image, .collection-hero__image'
    );

    if (heroImages.length === 0) return;

    var ticking = false;

    function updateParallax() {
      var scrolled = window.pageYOffset;

      for (var i = 0; i < heroImages.length; i++) {
        var img = heroImages[i];
        var rect = img.getBoundingClientRect();

        // Only apply parallax when the hero is visible in the viewport
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          var speed = 0.3;
          var yPos = scrolled * speed;
          img.style.transform = 'translateY(' + yPos + 'px) scale(1.1)';
        }
      }

      ticking = false;
    }

    window.addEventListener('scroll', function() {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });
  }


  // ===================================================================
  // STICKY HEADER
  // ===================================================================
  // Adds/removes `.header--scrolled` on the site header based on
  // scroll position. The CSS applies backdrop blur, lighter background,
  // and a subtle shadow when scrolled.
  // ===================================================================

  function initStickyHeader() {
    var header = document.querySelector('.site-header');
    if (!header) return;

    var ticking = false;

    function updateHeader() {
      var scrollY = window.pageYOffset;

      if (scrollY > 50) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }

      ticking = false;
    }

    window.addEventListener('scroll', function() {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }, { passive: true });

    // Run once on load in case the page is already scrolled
    updateHeader();
  }


  // ===================================================================
  // CART PULSE
  // ===================================================================
  // When a product is added to the cart, Debut shows a success
  // notification bar. We observe that notification's aria-hidden
  // attribute and trigger a pulse animation on the cart icon.
  // Also watches the cart indicator dot for class changes.
  // ===================================================================

  function initCartPulse() {
    var notification = document.getElementById('NotificationSuccess');
    var cartIcon = document.querySelector('.site-header__cart');

    if (!cartIcon) return;

    /**
     * Trigger the pulse animation on the cart icon.
     * Automatically removes the class when the animation completes.
     */
    function triggerPulse() {
      cartIcon.classList.add('cart-icon--pulse');
      cartIcon.addEventListener('animationend', function handler() {
        cartIcon.classList.remove('cart-icon--pulse');
        cartIcon.removeEventListener('animationend', handler);
      });
    }

    // --- Watch the notification bar ---
    if (notification && ('MutationObserver' in window)) {
      var notifObserver = new MutationObserver(function(mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var mutation = mutations[m];
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'aria-hidden'
          ) {
            if (notification.getAttribute('aria-hidden') === 'false') {
              triggerPulse();
            }
          }
        }
      });

      notifObserver.observe(notification, { attributes: true });
    }

    // --- Watch the cart indicator dot ---
    var cartIndicator = document.querySelector('.site-header__cart-indicator');
    if (cartIndicator && ('MutationObserver' in window)) {
      var indicatorObserver = new MutationObserver(function() {
        if (!cartIndicator.classList.contains('hide')) {
          triggerPulse();
        }
      });

      indicatorObserver.observe(cartIndicator, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  // ===================================================================
  // MEDIA LINK UNFRULER (Auto-embeds URLs as premium cards)
  // ===================================================================
  // Finds raw URLs pasted in the Rich Text Editor (like on the Media page)
  // and converts them into beautiful gallery-style article cards.
  function initMediaCards() {
    var links = document.querySelectorAll('.rte a');
    
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      var url = link.href;
      var text = link.innerText.trim();
      
      // Only process links where the text is exactly the URL (raw links)
      // and it looks like a news/article link from common domains
      if (text === url && (
        url.indexOf('ftvnews.com.tw') > -1 || 
        url.indexOf('taipeitimes.com') > -1 || 
        url.indexOf('ltn.com.tw') > -1 || 
        url.indexOf('cycu.edu.tw') > -1 || 
        url.indexOf('artemperor.tw') > -1 || 
        url.indexOf('focustaiwan.tw') > -1 ||
        url.indexOf('beanfun.com') > -1
      )) {
        
        var card = document.createElement('a');
        card.href = url;
        card.target = '_blank';
        card.rel = 'noopener noreferrer';
        card.className = 'media-embed-card'; // Removed gallery-reveal to prevent opacity:0 bug
        
        var domain = 'News Article';
        try {
          domain = new URL(url).hostname.replace('www.', '');
        } catch(e) {}
        
        card.innerHTML = 
          '<span class="media-embed-card__domain">' + domain + '</span>' +
          '<h4 class="media-embed-card__title">Read Article on ' + domain + '</h4>' +
          '<span class="media-embed-card__action">Read Full Story &rarr;</span>';
          
        link.parentNode.replaceChild(card, link);
      }
    }
  }

})();
